import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { AdminLayout } from "@/components/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { UserAvatar } from "@/components/UserAvatar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import {
  ArrowLeft,
  Calendar,
  MessageSquare,
  Gamepad2,
  Key,
  Building2,
  Users,
  Mail,
  Globe,
  Coins,
  Trophy,
  Heart,
  Star,
  Shield,
  CheckCircle2,
  XCircle,
  ExternalLink,
  Activity,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Filter,
  Ban,
  ShieldCheck,
  Crown,
  Sparkles,
  Zap
} from "lucide-react";
import { Gems } from "@/components/Gems";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { getDeveloperDisplayName } from "@/lib/developerUtils";
import { SteamProfileBlock } from "@/components/SteamProfileBlock";
import { getLevelFromXp } from "@/data/leveling";
import { PaymentsManagement } from "@/components/PaymentsManagement";
import { awardLevelRewards } from "@/services/pointsService";

interface MemberDetail {
  id: string;
  display_name: string;
  studio_name?: string | null;
  avatar_url?: string | null;
  user_type: "creator" | "developer" | "player";
  created_at: string;
  bio?: string | null;
  website_url?: string | null;
  studio_slug?: string | null;
  studio_logo_url?: string | null;
  is_admin?: boolean | null;
  is_banned?: boolean | null;
  is_verified?: boolean | null;
  is_premium?: boolean | null;
  premium_end_date?: string | null;
  email?: string | null;
  // Social media
  twitch_url?: string | null;
  youtube_url?: string | null;
  tiktok_url?: string | null;
  instagram_url?: string | null;
  twitter_url?: string | null;
  bluesky_url?: string | null;
  steam_url?: string | null;
  steam_id?: string | null;
  // Stats
  games_count: number;
  key_requests_count: number;
  messages_count: number;
  approved_requests: number;
  rejected_requests: number;
  pending_requests: number;
  last_activity?: string;
  // Player stats
  coins?: number;
  xp?: number;
  player_level?: number;
  trust_score?: number;
  level?: string;
  missions_done?: number;
  games_liked?: number;
  games_favorited?: number;
}

interface Game {
  id: string;
  title: string;
  description: string;
  cover_image_url?: string | null;
  created_at: string;
  is_published: boolean;
  genre?: string | null;
}

interface KeyRequest {
  id: string;
  status: string;
  created_at: string;
  game_id: string;
  game?: {
    title: string;
    cover_image_url?: string | null;
  };
}

interface Message {
  id: string;
  message: string;
  created_at: string;
  sender_id: string;
  receiver_id: string;
  game_id?: string;
  sender?: {
    display_name: string;
    avatar_url?: string | null;
    studio_name?: string | null;
  };
  receiver?: {
    display_name: string;
    avatar_url?: string | null;
    studio_name?: string | null;
  };
}

interface Activity {
  id: string;
  type: 'game_created' | 'game_published' | 'key_request_created' | 'key_request_approved' | 'key_request_rejected' | 'message_sent' | 'profile_updated' | 'mission_completed' | 'game_liked' | 'game_favorited' | 'developer_liked';
  description: string;
  created_at: string;
  metadata?: any;
}

export default function AdminMemberDetail() {
  const { memberId } = useParams<{ memberId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [member, setMember] = useState<MemberDetail | null>(null);
  const [games, setGames] = useState<Game[]>([]);
  const [keyRequests, setKeyRequests] = useState<KeyRequest[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isEditingCoins, setIsEditingCoins] = useState(false);
  const [newCoinsAmount, setNewCoinsAmount] = useState(0);
  const [isEditingPremium, setIsEditingPremium] = useState(false);
  const [premiumData, setPremiumData] = useState({
    is_premium: false,
    premium_end_date: ''
  });
  const [shopItems, setShopItems] = useState<any[]>([]);
  const [userInventory, setUserInventory] = useState<any[]>([]);
  const [isGivingItem, setIsGivingItem] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string>("");
  const [isProcessingItem, setIsProcessingItem] = useState(false);
  const [isEditingXp, setIsEditingXp] = useState(false);
  const [newXpAmount, setNewXpAmount] = useState(0);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState({
    activity: 40,
    games: 10,
    messages: 40,
    requests: 10
  });

  // Filter states
  const [dateFilter, setDateFilter] = useState({
    from: '',
    to: ''
  });

  // Filtered data states
  const [filteredActivities, setFilteredActivities] = useState<Activity[]>([]);
  const [filteredGames, setFilteredGames] = useState<Game[]>([]);
  const [filteredMessages, setFilteredMessages] = useState<Message[]>([]);
  const [filteredKeyRequests, setFilteredKeyRequests] = useState<KeyRequest[]>([]);

  useEffect(() => {
    if (user?.id) {
      checkAdminAccess();
    }
  }, [user?.id]);

  useEffect(() => {
    if (isAdmin && memberId) {
      fetchMemberData();
    }
  }, [isAdmin, memberId]);

  // Filter and paginate data when raw data or filters change
  useEffect(() => {
    filterAndPaginateData();
  }, [activities, games, messages, keyRequests, dateFilter, currentPage]);

  const filterAndPaginateData = () => {
    const filterByDate = (items: any[]) => {
      if (!dateFilter.from && !dateFilter.to) return items;

      return items.filter(item => {
        const itemDate = new Date(item.created_at);
        const fromDate = dateFilter.from ? new Date(dateFilter.from) : null;
        const toDate = dateFilter.to ? new Date(dateFilter.to) : null;

        if (fromDate && itemDate < fromDate) return false;
        if (toDate && itemDate > toDate) return false;
        return true;
      });
    };

    // Filter activities
    const filteredActivitiesData = filterByDate(activities);
    setFilteredActivities(filteredActivitiesData);

    // Filter games
    const filteredGamesData = filterByDate(games);
    setFilteredGames(filteredGamesData);

    // Filter messages
    const filteredMessagesData = filterByDate(messages);
    setFilteredMessages(filteredMessagesData);

    // Filter key requests
    const filteredKeyRequestsData = filterByDate(keyRequests);
    setFilteredKeyRequests(filteredKeyRequestsData);
  };

  const getPaginatedData = (data: any[], type: keyof typeof itemsPerPage) => {
    const startIndex = (currentPage - 1) * itemsPerPage[type];
    const endIndex = startIndex + itemsPerPage[type];
    return data.slice(startIndex, endIndex);
  };

  const getTotalPages = (data: any[], type: keyof typeof itemsPerPage) => {
    return Math.ceil(data.length / itemsPerPage[type]);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleDateFilterChange = (field: 'from' | 'to', value: string) => {
    setDateFilter(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const clearFilters = () => {
    setDateFilter({ from: '', to: '' });
    setCurrentPage(1);
  };

  // Pagination component
  const Pagination = ({ data, type }: { data: any[], type: keyof typeof itemsPerPage }) => {
    const totalPages = getTotalPages(data, type);
    if (totalPages <= 1) return null;

    return (
      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-muted-foreground">
          Showing {Math.min((currentPage - 1) * itemsPerPage[type] + 1, data.length)} to {Math.min(currentPage * itemsPerPage[type], data.length)} of {data.length} items
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  };

  // Date filter component
  const DateFilter = () => (
    <div className="mb-6 flex justify-start">
      <Card className="w-fit">
        <CardContent className="p-4">
          <div className="flex items-end gap-4">
            <div className="w-48">
              <Label htmlFor="date-from" className="text-sm font-medium">From</Label>
              <Input
                id="date-from"
                type="date"
                value={dateFilter.from}
                onChange={(e) => handleDateFilterChange('from', e.target.value)}
                className="mt-1"
              />
            </div>
            <div className="w-48">
              <Label htmlFor="date-to" className="text-sm font-medium">To</Label>
              <Input
                id="date-to"
                type="date"
                value={dateFilter.to}
                onChange={(e) => handleDateFilterChange('to', e.target.value)}
                className="mt-1"
              />
            </div>
            <Button variant="outline" onClick={clearFilters} className="h-10">
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const checkAdminAccess = async () => {
    if (!user) return;

    try {
      const { data } = await supabase
        .from("profiles")
        .select("is_admin, display_name")
        .eq("id", user.id)
        .single();

      // Fallback to display_name check if is_admin column doesn't exist yet
      if (data?.is_admin || data?.display_name === 'miikeu') {
        setIsAdmin(true);
      } else {
        toast.error("Access denied. Admin privileges required.");
        navigate("/dashboard");
      }
    } catch (error) {
      // Fallback: allow miikeu user even if there's a DB error
      if (user.email?.includes('miikeu') || user.user_metadata?.display_name === 'miikeu') {
        setIsAdmin(true);
      } else {
        toast.error("Error checking admin access");
        navigate("/dashboard");
      }
    }
  };

  const toggleAdminStatus = async () => {
    if (!member) return;

    try {
      const { error } = await supabase
        .from("profiles")
        .update({ is_admin: !member.is_admin })
        .eq("id", member.id);

      if (error) throw error;

      // Update local state
      setMember(prev => prev ? { ...prev, is_admin: !prev.is_admin } : null);

      toast.success(`Member ${!member.is_admin ? 'promoted to admin' : 'removed from admin'}`);
    } catch (error) {
      toast.error("Failed to update admin status");
    }
  };

  const toggleVerifiedStatus = async () => {
    if (!member) return;

    try {
      const { error } = await supabase
        .from("profiles")
        .update({ is_verified: !member.is_verified })
        .eq("id", member.id);

      if (error) throw error;

      // Update local state
      setMember(prev => prev ? { ...prev, is_verified: !prev.is_verified } : null);

      toast.success(`Member ${!member.is_verified ? 'verified' : 'unverified'}`);
    } catch (error) {
      toast.error("Failed to update verified status");
    }
  };

  const toggleBanStatus = async () => {
    if (!member) return;

    try {
      const { error } = await supabase
        .from("profiles")
        .update({ is_banned: !member.is_banned })
        .eq("id", member.id);

      if (error) throw error;

      // Update local state
      setMember(prev => prev ? { ...prev, is_banned: !prev.is_banned } : null);

      toast.success(`Member ${!member.is_banned ? 'banned' : 'unbanned'} successfully`);
    } catch (error) {
      toast.error("Failed to update ban status");
    }
  };

  const savePremiumStatus = async () => {
    if (!member) return;

    try {
      const updateData: any = {
        is_premium: premiumData.is_premium
      };

      if (premiumData.premium_end_date) {
        updateData.premium_end_date = new Date(premiumData.premium_end_date).toISOString();
      } else {
        updateData.premium_end_date = null;
      }

      const { error } = await supabase
        .from("profiles")
        .update(updateData)
        .eq("id", member.id);

      if (error) throw error;

      // Update local state
      setMember(prev => prev ? {
        ...prev,
        is_premium: premiumData.is_premium,
        premium_end_date: updateData.premium_end_date
      } : null);

      setIsEditingPremium(false);
      toast.success("Premium status updated successfully");
    } catch (error) {
      toast.error("Failed to update premium status");
    }
  };

  const saveCoinsAmount = async () => {
    if (!memberId) return;

    try {
      const { error } = await supabase
        .from("profiles")
        .update({ coins: newCoinsAmount })
        .eq("id", memberId);

      if (error) throw error;

      // Update local state
      setMember(prev => prev ? { ...prev, coins: newCoinsAmount } : null);
      setIsEditingCoins(false);
      toast.success("Gems amount updated successfully");
    } catch (error) {
      console.error("Error updating coins:", error);
      toast.error("Failed to update coins amount");
    }
  };

  const saveXpAmount = async (reset: boolean = false) => {
    if (!memberId || !member) return;

    const amount = reset ? 0 : newXpAmount;
    const oldLevel = member.player_level || 1;
    const newLevel = getLevelFromXp(amount);

    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          xp: amount,
          player_level: newLevel
        })
        .eq("id", memberId);

      if (error) throw error;

      // Process rewards for each level gained if not resetting
      if (!reset && newLevel > oldLevel) {
        toast.info(`Awarding rewards for ${newLevel - oldLevel} levels...`);
        for (let l = oldLevel + 1; l <= newLevel; l++) {
          await awardLevelRewards(memberId, l);
        }
      }

      // Update local state
      setMember(prev => prev ? { ...prev, xp: amount, player_level: newLevel } : null);
      setIsEditingXp(false);
      toast.success(reset ? "XP reset successfully" : "XP updated successfully");
    } catch (error) {
      console.error("Error updating XP:", error);
      toast.error(reset ? "Failed to reset XP" : "Failed to update XP");
    }
  };

  const fetchInventoryData = async () => {
    if (!memberId) return;
    const { data: inventory } = await supabase
      .from('user_inventory')
      .select('*, shop_items(*)')
      .eq('user_id', memberId);
    setUserInventory(inventory || []);
  };

  const giveItem = async () => {
    if (!memberId || !selectedItemId) return;

    try {
      setIsProcessingItem(true);
      const item = shopItems.find(i => i.id === selectedItemId);

      if (item?.type === 'border') {
        // check if user already has this border
        const { data: existing, error: searchError } = await supabase
          .from('user_inventory')
          .select('*')
          .eq('user_id', memberId)
          .eq('item_id', selectedItemId)
          .maybeSingle();

        if (searchError && searchError.code !== 'PGRST116') throw searchError;

        if (existing) {
          toast.info("User already has this border");
          setIsGivingItem(false);
          return;
        }
      }

      // Add to inventory
      const { error: inventoryError } = await supabase
        .from('user_inventory')
        .insert({
          user_id: memberId,
          item_id: selectedItemId,
          item_type: item?.type, // Added for database consistency and indexing
          is_active: item?.type === 'boost', // Mark boosts as active immediately
          acquired_at: new Date().toISOString()
        });

      if (inventoryError) throw inventoryError;

      // If it's a boost, process it to update profile
      if (item?.type === 'boost') {
        const durationDays = item.metadata?.duration_days || 0;
        const { error: boostError } = await supabase.rpc('process_player_boost', {
          p_user_id: memberId,
          p_duration_days: durationDays
        });
        if (boostError) throw boostError;
      }

      toast.success(`${item?.name || 'Item'} given successfully`);
      setIsGivingItem(false);
      fetchInventoryData(); // Refresh inventory list
    } catch (error: any) {
      console.error("Error giving item:", error);
      toast.error(error.message || "Failed to give item");
    } finally {
      setIsProcessingItem(false);
    }
  };

  const fetchMemberData = async () => {
    if (!memberId) return;

    try {
      setLoading(true);

      // Fetch member profile
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select(`
          id,
          display_name,
          studio_name,
          avatar_url,
          user_type,
          created_at,
          bio,
          website_url,
          studio_slug,
          studio_logo_url,
          is_admin,
          is_banned,
          is_verified,
          is_premium,
          premium_end_date,
          twitch_url,
          youtube_url,
          tiktok_url,
          instagram_url,
          twitter_url,
          bluesky_url,
          steam_url,
          steam_id,
          active_border_id,
          shop_items(metadata),
          xp,
          player_level
        `)
        .eq("id", memberId)
        .single();

      if (profileError) {
        throw profileError;
      }

      // Fetch user email using SQL function
      let userEmail = null;
      try {
        const { data, error: emailError } = await supabase.rpc('get_user_email', { user_id: memberId });
        if (!emailError && data) {
          userEmail = data;
        }
      } catch (error) {
        console.error("Error fetching user email:", error);
      }

      // Fetch games (for developers)
      const { data: gamesData, error: gamesError } = await supabase
        .from("games")
        .select(`
          id,
          title,
          description,
          cover_image_url,
          created_at,
          is_published,
          genre
        `)
        .eq("developer_id", memberId)
        .order("created_at", { ascending: false });

      if (gamesError) {
        throw gamesError;
      }

      // Fetch key requests (for creators) with game info
      const { data: keyRequestsData, error: keyRequestsError } = await supabase
        .from("key_requests")
        .select(`
          id,
          status,
          created_at,
          game_id,
          game:games(title, cover_image_url)
        `)
        .eq("creator_id", memberId)
        .order("created_at", { ascending: false });

      if (keyRequestsError) {
        console.error('Key requests error:', keyRequestsError);
      }

      // Fetch recent messages sent by the user only
      const { data: messagesData, error: messagesError } = await supabase
        .from("messages")
        .select(`
          id,
          message,
          created_at,
          sender_id,
          receiver_id,
          game_id,
          sender:profiles!messages_sender_id_fkey(display_name, avatar_url, studio_name),
          receiver:profiles!messages_receiver_id_fkey(display_name, avatar_url, studio_name)
        `)
        .eq("sender_id", memberId)
        .order("created_at", { ascending: false })
        .limit(20);

      if (messagesError) {
        // Try a simpler query if the complex one fails
        const { data: simpleMessagesData, error: simpleMessagesError } = await supabase
          .from("messages")
          .select(`
             id,
             message,
             created_at,
             sender_id,
             receiver_id
           `)
          .eq("sender_id", memberId)
          .order("created_at", { ascending: false })
          .limit(20);

        if (!simpleMessagesError) {
          // Use simple data if complex query failed
          setMessages(simpleMessagesData as any[]);
        } else {
          setMessages([] as any[]);
        }
      } else {
        setMessages(messagesData as any[]);
      }

      // Fetch player stats if user is a player
      let playerStats = {
        coins: 0,
        xp: 0,
        player_level: 1,
        trust_score: 0,
        level: 'Bronze',
        missions_done: 0,
        games_liked: 0,
        games_favorited: 0
      };

      // Fetch player activities
      let gameLikesData: any[] = [];
      let gameFavoritesData: any[] = [];
      let developerLikesData: any[] = [];
      let missionsData: any[] = [];

      if (profile.user_type === 'player') {
        try {
          const [
            { data: coinsData },
            { data: missionsResult },
            { data: likesResult },
            { data: favoritesResult },
            { data: devLikesResult }
          ] = await Promise.all([
            supabase.from("profiles").select("coins, trust_score, level").eq("id", memberId).single(),
            supabase.from("player_missions").select("id, completed_at, mission:missions(name)").eq("player_id", memberId),
            supabase.from("game_likes").select("id, created_at, game_id, game:games(title)").eq("player_id", memberId).order("created_at", { ascending: false }),
            supabase.from("game_favorites").select("id, created_at, game_id, game:games(title)").eq("player_id", memberId).order("created_at", { ascending: false }),
            supabase.from("developer_likes").select("id, created_at, developer_id, developer:profiles!developer_likes_developer_id_fkey(display_name, studio_name)").eq("player_id", memberId).order("created_at", { ascending: false })
          ]);

          playerStats = {
            coins: coinsData?.coins || 0,
            xp: coinsData?.xp || 0,
            player_level: coinsData?.player_level || 1,
            trust_score: coinsData?.trust_score || 0,
            level: coinsData?.level || 'Bronze',
            missions_done: missionsResult?.length || 0,
            games_liked: likesResult?.length || 0,
            games_favorited: favoritesResult?.length || 0
          };

          missionsData = missionsResult || [];
          gameLikesData = likesResult || [];
          gameFavoritesData = favoritesResult || [];
          developerLikesData = devLikesResult || [];
        } catch (error) {
          console.error("Error fetching player stats:", error);
        }
      }

      // Calculate stats
      const games_count = gamesData?.length || 0;
      const key_requests_count = keyRequestsData?.length || 0;
      const messages_count = messagesData?.length || 0;
      const approved_requests = keyRequestsData?.filter(k => k.status === "approved").length || 0;
      const rejected_requests = keyRequestsData?.filter(k => k.status === "rejected").length || 0;
      const pending_requests = keyRequestsData?.filter(k => k.status === "pending").length || 0;

      // Generate activities
      const activitiesList: Activity[] = [];

      // Add game activities
      gamesData?.forEach(game => {
        activitiesList.push({
          id: `game-${game.id}`,
          type: game.is_published ? 'game_published' : 'game_created',
          description: game.is_published ? `Published game "${game.title}"` : `Created game "${game.title}"`,
          created_at: game.created_at,
          metadata: { game_id: game.id, game_title: game.title }
        });
      });

      // Add key request activities
      keyRequestsData?.forEach(request => {
        const gameTitle = request.game?.title || `Game ${request.game_id}`;
        activitiesList.push({
          id: `key-request-${request.id}`,
          type: 'key_request_created',
          description: `Created key request for "${gameTitle}"`,
          created_at: request.created_at,
          metadata: { request_id: request.id, game_id: request.game_id, status: request.status, game_title: gameTitle }
        });
      });

      // Add message activities
      messagesData?.forEach(message => {
        const receiverName = message.receiver?.studio_name || message.receiver?.display_name || `User ${message.receiver_id}`;
        activitiesList.push({
          id: `message-${message.id}`,
          type: 'message_sent',
          description: `Sent message to ${receiverName}`,
          created_at: message.created_at,
          metadata: { message_id: message.id, receiver_id: message.receiver_id, receiver_name: receiverName }
        });
      });

      // Add player activities
      if (profile.user_type === 'player') {
        // Add mission activities
        missionsData?.forEach(mission => {
          activitiesList.push({
            id: `mission-${mission.id}`,
            type: 'mission_completed',
            description: `Completed mission: ${mission.mission?.name || 'Unknown Mission'}`,
            created_at: mission.completed_at,
            metadata: { mission_id: mission.id, mission_name: mission.mission?.name }
          });
        });

        // Add game like activities
        gameLikesData?.forEach(like => {
          activitiesList.push({
            id: `game-like-${like.id}`,
            type: 'game_liked',
            description: `Liked game: ${like.game?.title || 'Unknown Game'}`,
            created_at: like.created_at,
            metadata: { like_id: like.id, game_id: like.game_id, game_title: like.game?.title }
          });
        });

        // Add game favorite activities
        gameFavoritesData?.forEach(favorite => {
          activitiesList.push({
            id: `game-favorite-${favorite.id}`,
            type: 'game_favorited',
            description: `Favorited game: ${favorite.game?.title || 'Unknown Game'}`,
            created_at: favorite.created_at,
            metadata: { favorite_id: favorite.id, game_id: favorite.game_id, game_title: favorite.game?.title }
          });
        });

        // Add developer like activities
        developerLikesData?.forEach(like => {
          const devName = like.developer?.studio_name || like.developer?.display_name || 'Unknown Developer';
          activitiesList.push({
            id: `dev-like-${like.id}`,
            type: 'developer_liked',
            description: `Liked developer: ${devName}`,
            created_at: like.created_at,
            metadata: { like_id: like.id, developer_id: like.developer_id, developer_name: devName }
          });
        });
      }

      // Sort activities by date
      activitiesList.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      // Find last activity
      const lastActivity = activitiesList.length > 0
        ? activitiesList[0].created_at
        : profile.created_at;

      setMember({
        ...profile,
        email: userEmail,
        is_premium: profile.is_premium || false,
        premium_end_date: profile.premium_end_date || null,
        games_count,
        key_requests_count,
        messages_count,
        approved_requests,
        rejected_requests,
        pending_requests,
        last_activity: lastActivity,
        // Player stats
        coins: playerStats.coins,
        trust_score: playerStats.trust_score,
        level: playerStats.level,
        missions_done: playerStats.missions_done,
        games_liked: playerStats.games_liked,
        games_favorited: playerStats.games_favorited
      });

      setGames(gamesData || []);
      setKeyRequests(keyRequestsData || []);
      setMessages(messagesData || []);
      setActivities(activitiesList);

      // Fetch shop items and inventory
      const { data: shopItemsData } = await supabase
        .from('shop_items')
        .select('*')
        .eq('is_active', true);
      setShopItems(shopItemsData || []);

      fetchInventoryData();
    } catch (error) {
      toast.error("Failed to load member data");
    } finally {
      setLoading(false);
    }
  };

  const getDisplayName = (member: MemberDetail) => {
    return getDeveloperDisplayName(member);
  };

  const getInitial = (member: MemberDetail) => {
    const name = getDisplayName(member);
    return name.charAt(0).toUpperCase();
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "approved":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "rejected":
        return <XCircle className="w-4 h-4 text-red-500" />;
      case "pending":
        return <Clock className="w-4 h-4 text-yellow-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="container mx-auto">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
            <p className="text-muted-foreground">You don't have permission to access this page.</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="container mx-auto">
          <div className="text-center py-20">
            <Gamepad2 className="w-8 h-8 animate-spin text-primary mx-auto" />
            <p className="mt-2 text-muted-foreground">Loading member data...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!member) {
    return (
      <AdminLayout>
        <div className="container mx-auto">
          <div className="text-center py-20">
            <h1 className="text-2xl font-bold mb-4">Member Not Found</h1>
            <p className="text-muted-foreground">The requested member could not be found.</p>
            <Button onClick={() => navigate("/admin/members")} className="mt-4">
              Back to Members
            </Button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <UserAvatar
                src={member.avatar_url || undefined}
                fallback={getInitial(member)}
                size="lg"
                borderStyle={(member as any).shop_items?.metadata?.style}
              />
              <div>
                <h1 className="text-3xl font-bold">{getDisplayName(member)}</h1>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant={member.user_type === "developer" ? "default" : member.user_type === "creator" ? "secondary" : "outline"}>
                    {member.user_type === "developer" ? (
                      <>
                        <Building2 className="w-3 h-3 mr-1" />
                        Developer
                      </>
                    ) : member.user_type === "creator" ? (
                      <>
                        <Users className="w-3 h-3 mr-1" />
                        Creator
                      </>
                    ) : (
                      <>
                        <Gamepad2 className="w-3 h-3 mr-1" />
                        Player
                      </>
                    )}
                  </Badge>
                  {member.is_verified && (
                    <Badge variant="outline" className="bg-blue-500 hover:bg-blue-600 text-white border-blue-500">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Verified
                    </Badge>
                  )}
                  {member.is_admin && (
                    <Badge variant="destructive" className="bg-purple-500 hover:bg-purple-600">
                      <Shield className="w-3 h-3 mr-1" />
                      Admin
                    </Badge>
                  )}
                  {member.is_banned && (
                    <Badge variant="destructive" className="bg-red-500 hover:bg-red-600">
                      <Ban className="w-3 h-3 mr-1" />
                      Banned
                    </Badge>
                  )}
                  <span className="text-sm text-muted-foreground">
                    Joined {new Date(member.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Admin Actions */}
        <div className="flex justify-end gap-2 mb-6">
          {user && member && user.id !== member.id && (
            <Button
              variant={member.is_verified ? "default" : "outline"}
              onClick={toggleVerifiedStatus}
              className={`flex items-center gap-2 ${member.is_verified ? 'bg-blue-500 hover:bg-blue-600 text-white' : ''}`}
            >
              {member.is_verified ? (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Unverify
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  Verify User
                </>
              )}
            </Button>
          )}
          {user && member && user.id !== member.id && (
            <Button
              variant={member.is_admin ? "destructive" : "outline"}
              onClick={toggleAdminStatus}
              className="flex items-center gap-2"
            >
              {member.is_admin ? (
                <>
                  <Shield className="w-4 h-4" />
                  Remove Admin
                </>
              ) : (
                <>
                  <ShieldCheck className="w-4 h-4" />
                  Make Admin
                </>
              )}
            </Button>
          )}
          {user && member && user.id !== member.id && (
            <Button
              variant={member.is_banned ? "outline" : "destructive"}
              onClick={toggleBanStatus}
              className="flex items-center gap-2"
            >
              {member.is_banned ? (
                <>
                  <Shield className="w-4 h-4" />
                  Unban User
                </>
              ) : (
                <>
                  <Ban className="w-4 h-4" />
                  Ban User
                </>
              )}
            </Button>
          )}
          {user && member && user.id === member.id && (
            <div className="text-sm text-muted-foreground italic">
              You cannot modify your own admin or ban status
            </div>
          )}
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
            {member.user_type === 'developer' && (
              <TabsTrigger value="games">Games</TabsTrigger>
            )}
            {(member.user_type === 'creator' || member.user_type === 'developer') && (
              <TabsTrigger value="requests">Key Requests</TabsTrigger>
            )}
            {member.user_type === 'developer' && (
              <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
            )}
            <TabsTrigger value="messages">Messages</TabsTrigger>
            {member.user_type === 'player' && (
              <TabsTrigger value="inventory">Inventory</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {member.user_type === 'developer' && (
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Games</CardTitle>
                    <Gamepad2 className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{member.games_count}</div>
                    <p className="text-xs text-muted-foreground">Published games</p>
                  </CardContent>
                </Card>
              )}

              {(member.user_type === 'creator' || member.user_type === 'developer') && (
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Key Requests</CardTitle>
                    <Key className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{member.key_requests_count}</div>
                    <div className="flex gap-1 mt-1">
                      <Badge variant="default" className="text-xs">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        {member.approved_requests}
                      </Badge>
                      <Badge variant="destructive" className="text-xs">
                        <XCircle className="w-3 h-3 mr-1" />
                        {member.rejected_requests}
                      </Badge>
                      <Badge variant="secondary" className="text-xs">
                        <Clock className="w-3 h-3 mr-1" />
                        {member.pending_requests}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Messages</CardTitle>
                  <MessageSquare className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{member.messages_count}</div>
                  <p className="text-xs text-muted-foreground">Total messages</p>
                </CardContent>
              </Card>

              {member.user_type === 'player' && (
                <>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Gems</CardTitle>
                      <div className="flex items-center gap-2">
                        <Gems />
                        <Dialog open={isEditingCoins} onOpenChange={setIsEditingCoins}>
                          <DialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-6 px-2 text-xs"
                              onClick={() => setNewCoinsAmount(member.coins || 0)}
                            >
                              Edit
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="sm:max-w-md">
                            <DialogHeader>
                              <DialogTitle>Edit Gems Amount</DialogTitle>
                            </DialogHeader>
                            <div className="grid gap-4 py-4">
                              <div className="grid gap-2">
                                <Label htmlFor="coins">New Gems Amount</Label>
                                <Input
                                  id="coins"
                                  type="number"
                                  min="0"
                                  value={newCoinsAmount}
                                  onChange={(e) => setNewCoinsAmount(parseInt(e.target.value) || 0)}
                                />
                              </div>
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="outline"
                                  onClick={() => setIsEditingCoins(false)}
                                >
                                  Cancel
                                </Button>
                                <Button onClick={saveCoinsAmount}>
                                  Save
                                </Button>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{member.coins || 0}</div>
                      <p className="text-xs text-muted-foreground">Total coins earned</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Player Level & XP</CardTitle>
                      <div className="flex items-center gap-2">
                        <Dialog open={isEditingXp} onOpenChange={setIsEditingXp}>
                          <DialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-6 px-2 text-xs"
                              onClick={() => setNewXpAmount(member.xp || 0)}
                            >
                              Edit
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="sm:max-w-md">
                            <DialogHeader>
                              <DialogTitle>Edit Player XP</DialogTitle>
                              <DialogDescription>
                                Manually set the player's experience points.
                              </DialogDescription>
                            </DialogHeader>
                            <div className="grid gap-4 py-4">
                              <div className="grid gap-2">
                                <Label htmlFor="xp">New XP Amount</Label>
                                <Input
                                  id="xp"
                                  type="number"
                                  min="0"
                                  value={newXpAmount}
                                  onChange={(e) => setNewXpAmount(parseInt(e.target.value) || 0)}
                                />
                              </div>
                              <div className="flex justify-between gap-2">
                                <Button
                                  variant="destructive"
                                  onClick={() => saveXpAmount(true)}
                                >
                                  Reset to 0
                                </Button>
                                <div className="flex gap-2">
                                  <Button
                                    variant="outline"
                                    onClick={() => setIsEditingXp(false)}
                                  >
                                    Cancel
                                  </Button>
                                  <Button onClick={() => saveXpAmount(false)}>
                                    Save
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                        <Zap className="h-4 w-4 text-blue-500" />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">Lvl {member.player_level || 1}</div>
                      <p className="text-xs text-muted-foreground">{member.xp || 0} Total XP</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Trust Score</CardTitle>
                      <Shield className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{member.trust_score || 0}/100</div>
                      <p className="text-xs text-muted-foreground">Level: {member.level || 'Bronze'}</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Missions</CardTitle>
                      <Trophy className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{member.missions_done || 0}</div>
                      <p className="text-xs text-muted-foreground">Missions completed</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Games Liked</CardTitle>
                      <Heart className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{member.games_liked || 0}</div>
                      <p className="text-xs text-muted-foreground">Games liked</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Games Favorited</CardTitle>
                      <Star className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{member.games_favorited || 0}</div>
                      <p className="text-xs text-muted-foreground">Games favorited</p>
                    </CardContent>
                  </Card>
                </>
              )}

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Last Activity</CardTitle>
                  <Activity className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-sm font-medium">
                    {member.last_activity ? new Date(member.last_activity).toLocaleDateString() : "Never"}
                  </div>
                  <p className="text-xs text-muted-foreground">Most recent action</p>
                </CardContent>
              </Card>
            </div>

            {/* Profile Information */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Profile Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {member.email && (
                    <div>
                      <h4 className="font-medium mb-1 flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        Email
                      </h4>
                      <p className="text-sm text-muted-foreground">{member.email}</p>
                    </div>
                  )}
                  {member.bio && (
                    <div>
                      <h4 className="font-medium mb-1">Bio</h4>
                      <p className="text-sm text-muted-foreground">{member.bio}</p>
                    </div>
                  )}
                  {member.website_url && (
                    <div>
                      <h4 className="font-medium mb-1">Website</h4>
                      <a
                        href={member.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline flex items-center gap-1"
                      >
                        {member.website_url}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  )}
                  {member.studio_slug && (
                    <div>
                      <h4 className="font-medium mb-1">Studio URL</h4>
                      <a
                        href={`/developer/${member.studio_slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline flex items-center gap-1"
                      >
                        /developer/{member.studio_slug}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Social Media</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2">
                    {member.twitch_url && (
                      <a
                        href={member.twitch_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 px-3 py-2 text-sm bg-purple-500/10 border border-purple-500/20 text-purple-600 hover:bg-purple-500/20 hover:text-purple-700 rounded-custom-paddings transition-colors"
                      >
                        <svg className="w-4 h-4 text-purple-500" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z" />
                        </svg>
                        Twitch
                      </a>
                    )}
                    {member.youtube_url && (
                      <a
                        href={member.youtube_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 px-3 py-2 text-sm bg-red-500/10 border border-red-500/20 text-red-600 hover:bg-red-500/20 hover:text-red-700 rounded-custom-paddings transition-colors"
                      >
                        <svg className="w-4 h-4 text-red-500" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                        </svg>
                        YouTube
                      </a>
                    )}
                    {member.tiktok_url && (
                      <a
                        href={member.tiktok_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 px-3 py-2 text-sm bg-black/10 border border-black/20 text-black hover:bg-black/20 hover:text-gray-800 rounded-custom-paddings transition-colors"
                      >
                        <svg className="w-4 h-4 text-black" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.03 1.75.31 3.49 1.02 5.09.81 1.7 2.25 2.98 4.06 3.51 1.8.53 3.75.2 5.35-.9 1.5-1.03 2.4-2.65 2.61-4.31.07-.52.04-1.05.04-1.57.02-1.65.02-3.3.02-4.95-.01-2.2-.01-4.4.02-6.6.01-.25-.02-.5-.05-.75-.18-1.16-.93-2.18-1.95-2.73-.82-.44-1.73-.66-2.65-.78-.46-.06-.92-.1-1.38-.16-.2-.02-.4-.05-.6-.08-.17-.02-.34-.04-.51-.05-.09-.01-.18-.02-.27-.03z" />
                        </svg>
                        TikTok
                      </a>
                    )}
                    {member.instagram_url && (
                      <a
                        href={member.instagram_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 px-3 py-2 text-sm bg-pink-500/10 border border-pink-500/20 text-pink-600 hover:bg-pink-500/20 hover:text-pink-700 rounded-custom-paddings transition-colors"
                      >
                        <svg className="w-4 h-4 text-pink-500" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                        </svg>
                        Instagram
                      </a>
                    )}
                    {member.twitter_url && (
                      <a
                        href={member.twitter_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 px-3 py-2 text-sm bg-blue-500/10 border border-blue-500/20 text-blue-600 hover:bg-blue-500/20 hover:text-blue-700 rounded-custom-paddings transition-colors"
                      >
                        <svg className="w-4 h-4 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                        </svg>
                        Twitter
                      </a>
                    )}
                    {member.bluesky_url && (
                      <a
                        href={member.bluesky_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 px-3 py-2 text-sm bg-sky-500/10 border border-sky-500/20 text-sky-600 hover:bg-sky-500/20 hover:text-sky-700 rounded-custom-paddings transition-colors"
                      >
                        <svg className="w-4 h-4 text-sky-500" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 10.8c-1.087-5.785-7.155-7.19-7.155-7.19s-1.22 1.197-1.22 2.584c0 1.386.995 2.584 2.18 2.584 1.184 0 1.22-1.197 1.22-1.197.3 3.451 2.895 5.785 5.975 5.785 3.08 0 5.675-2.334 5.975-5.785 0 0 .036 1.197 1.22 1.197 1.185 0 2.18-1.198 2.18-2.584 0-1.387-1.22-2.584-1.22-2.584s-6.068 1.405-7.155 7.19z" />
                        </svg>
                        Bluesky
                      </a>
                    )}
                    {member.steam_url && (
                      <a
                        href={member.steam_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium bg-gradient-to-r from-[#2a475e] to-[#3c5a6b] text-white rounded-custom-paddings hover:from-[#3c5a6b] hover:to-[#4a6b7a] transition-all duration-200 shadow-lg hover:shadow-xl border border-[#4a6b7a] ring-2 ring-[#2a475e]/20"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.031 4.524 4.527s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.004.105.004.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.627 0 11.999-5.373 11.999-12S18.605 0 11.979 0zM7.54 18.21l-1.473-.61c.262.543.714.999 1.314 1.25 1.297.539 2.793-.076 3.332-1.375.263-.63.264-1.319.005-1.949s-.75-1.121-1.377-1.383c-.624-.26-1.29-.249-1.878-.03l1.523.63c.956.4 1.409 1.5 1.009 2.455-.397.957-1.497 1.41-2.454 1.012H7.54zm11.415-9.303c0-1.662-1.353-3.015-3.015-3.015-1.665 0-3.015 1.353-3.015 3.015 0 1.665 1.35 3.015 3.015 3.015 1.663 0 3.015-1.35 3.015-3.015zm-5.273-.005c0-1.252 1.013-2.266 2.265-2.266 1.249 0 2.266 1.014 2.266 2.266 0 1.251-1.017 2.265-2.266 2.265-1.253 0-2.265-1.014-2.265-2.265z" />
                        </svg>
                        Steam
                      </a>
                    )}
                    {!member.twitch_url && !member.youtube_url && !member.tiktok_url && !member.instagram_url && !member.twitter_url && !member.bluesky_url && !member.steam_url && (
                      <div className="col-span-2 text-center py-4">
                        <p className="text-sm text-muted-foreground">No social media links</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Steam Profile Block */}
              {member.steam_id && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Gamepad2 className="w-4 h-4" />
                      Steam Profile
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <SteamProfileBlock
                      steamId={member.steam_id}
                      steamUrl={member.steam_url}
                    />
                  </CardContent>
                </Card>
              )}

              {/* Premium Status (for developers only) */}
              {member.user_type === 'developer' && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Crown className="w-4 h-4" />
                      Premium Status
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {!isEditingPremium ? (
                      <>
                        <div className="flex items-center justify-between">
                          <div>
                            <Label className="text-sm font-medium">Premium Status</Label>
                            <p className="text-sm text-muted-foreground">
                              {member.is_premium && (!member.premium_end_date || new Date(member.premium_end_date) > new Date())
                                ? 'Active'
                                : 'Inactive'}
                            </p>
                            {member.premium_end_date && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Expires: {new Date(member.premium_end_date).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setIsEditingPremium(true);
                              setPremiumData({
                                is_premium: member.is_premium || false,
                                premium_end_date: member.premium_end_date
                                  ? new Date(member.premium_end_date).toISOString().split('T')[0]
                                  : ''
                              });
                            }}
                          >
                            Edit
                          </Button>
                        </div>
                      </>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="is_premium"
                            checked={premiumData.is_premium}
                            onCheckedChange={(checked) =>
                              setPremiumData({ ...premiumData, is_premium: !!checked })
                            }
                          />
                          <Label htmlFor="is_premium" className="cursor-pointer">
                            Premium Studio
                          </Label>
                        </div>
                        {premiumData.is_premium && (
                          <div>
                            <Label htmlFor="premium_end_date">Premium End Date</Label>
                            <Input
                              id="premium_end_date"
                              type="date"
                              value={premiumData.premium_end_date}
                              onChange={(e) =>
                                setPremiumData({ ...premiumData, premium_end_date: e.target.value })
                              }
                              className="mt-1"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Leave empty for lifetime premium
                            </p>
                          </div>
                        )}
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={savePremiumStatus}
                          >
                            Save
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setIsEditingPremium(false);
                              setPremiumData({
                                is_premium: member.is_premium || false,
                                premium_end_date: member.premium_end_date
                                  ? new Date(member.premium_end_date).toISOString().split('T')[0]
                                  : ''
                              });
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="activity" className="space-y-6">
            <DateFilter />
            <Card>
              <CardHeader>
                <CardTitle>Recent Activity ({filteredActivities.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {filteredActivities.length > 0 ? (
                  <div className="space-y-4">
                    {getPaginatedData(filteredActivities, 'activity').map((activity) => (
                      <div key={activity.id} className="flex items-start gap-4 p-4 border rounded-custom-paddings">
                        <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                          {activity.type === 'game_created' || activity.type === 'game_published' ? (
                            <Gamepad2 className="w-4 h-4 text-blue-500" />
                          ) : activity.type === 'key_request_created' ? (
                            <Key className="w-4 h-4 text-yellow-500" />
                          ) : activity.type === 'message_sent' ? (
                            <MessageSquare className="w-4 h-4 text-green-500" />
                          ) : activity.type === 'mission_completed' ? (
                            <Trophy className="w-4 h-4 text-purple-500" />
                          ) : activity.type === 'game_liked' ? (
                            <Heart className="w-4 h-4 text-red-500" />
                          ) : activity.type === 'game_favorited' ? (
                            <Star className="w-4 h-4 text-yellow-500" />
                          ) : activity.type === 'developer_liked' ? (
                            <Users className="w-4 h-4 text-indigo-500" />
                          ) : (
                            <Activity className="w-4 h-4 text-gray-500" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{activity.description}</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(activity.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No recent activity</p>
                )}
                <Pagination data={filteredActivities} type="activity" />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="games" className="space-y-6">
            <DateFilter />
            <Card>
              <CardHeader>
                <CardTitle>Published Games ({filteredGames.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {filteredGames.length > 0 ? (
                  <div className="space-y-4">
                    {getPaginatedData(filteredGames, 'games').map((game) => (
                      <div
                        key={game.id}
                        className="flex items-center gap-4 p-4 border rounded-custom-paddings cursor-pointer hover:shadow-lg transition-shadow"
                        onClick={() => navigate(`/game/${game.id}`)}
                      >
                        {game.cover_image_url ? (
                          <img
                            src={game.cover_image_url}
                            alt={game.title}
                            className="w-16 h-16 object-cover rounded"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-muted rounded flex items-center justify-center">
                            <Gamepad2 className="w-8 h-8 text-muted-foreground" />
                          </div>
                        )}
                        <div className="flex-1">
                          <h3 className="font-semibold hover:text-primary transition-colors">{game.title}</h3>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant={game.is_published ? "default" : "secondary"}>
                              {game.is_published ? "Published" : "Draft"}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {new Date(game.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No games published yet</p>
                )}
                <Pagination data={filteredGames} type="games" />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="requests" className="space-y-6">
            <DateFilter />
            <Card>
              <CardHeader>
                <CardTitle>Key Requests ({filteredKeyRequests.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {filteredKeyRequests.length > 0 ? (
                  <div className="space-y-4">
                    {getPaginatedData(filteredKeyRequests, 'requests').map((request) => (
                      <div
                        key={request.id}
                        className="flex items-center gap-4 p-4 border rounded-custom-paddings cursor-pointer hover:shadow-lg transition-shadow"
                        onClick={() => navigate(`/game/${request.game_id}`)}
                      >
                        {request.game?.cover_image_url ? (
                          <img
                            src={request.game.cover_image_url}
                            alt={request.game.title}
                            className="w-16 h-16 object-cover rounded"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-muted rounded flex items-center justify-center">
                            <Key className="w-8 h-8 text-muted-foreground" />
                          </div>
                        )}
                        <div className="flex-1">
                          <h3 className="font-semibold hover:text-primary transition-colors">
                            {request.game?.title || `Game ID: ${request.game_id}`}
                          </h3>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge className={getStatusColor(request.status)}>
                              {getStatusIcon(request.status)}
                              <span className="ml-1 capitalize">{request.status}</span>
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {new Date(request.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No key requests made yet</p>
                )}
                <Pagination data={filteredKeyRequests} type="requests" />
              </CardContent>
            </Card>
          </TabsContent>

          {member.user_type === 'developer' && (
            <TabsContent value="subscriptions" className="space-y-6">
              <PaymentsManagement userId={member.id} isAdmin={true} onPaymentUpdate={fetchMemberData} />
            </TabsContent>
          )}

          <TabsContent value="messages" className="space-y-6">
            <DateFilter />
            <Card>
              <CardHeader>
                <CardTitle>Messages Sent ({filteredMessages.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {filteredMessages.length > 0 ? (
                  <div className="space-y-4">
                    {getPaginatedData(filteredMessages, 'messages').map((message) => (
                      <div key={message.id} className="p-4 border rounded-custom-paddings">
                        <div className="flex items-start gap-3">
                          <Avatar className="w-8 h-8">
                            <AvatarImage src={message.sender?.avatar_url || undefined} />
                            <AvatarFallback className="text-xs">
                              {(message.sender?.studio_name || message.sender?.display_name || 'U').charAt(0).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xs text-muted-foreground">To:</span>
                              <button
                                onClick={() => navigate(`/admin/members/${message.receiver_id}`)}
                                className="text-sm font-medium text-primary hover:underline transition-colors"
                              >
                                {message.receiver?.studio_name || message.receiver?.display_name || `User ${message.receiver_id}`}
                              </button>
                              <span className="text-xs text-muted-foreground">
                                {new Date(message.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-3">
                              {message.message}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No messages sent yet</p>
                )}
                <Pagination data={filteredMessages} type="messages" />
              </CardContent>
            </Card>
          </TabsContent>
          {member.user_type === 'player' && (
            <TabsContent value="inventory" className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">User Inventory ({userInventory.length})</h2>
                {member && (
                  <Dialog open={isGivingItem} onOpenChange={setIsGivingItem}>
                    <DialogTrigger asChild>
                      <Button className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4" />
                        Give Shop Item
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Give Shop Item to {member.display_name}</DialogTitle>
                      </DialogHeader>
                      <div className="grid gap-4 py-4">
                        <div className="space-y-2">
                          <Label htmlFor="item">Select Item</Label>
                          <select
                            id="item"
                            className="w-full p-2 rounded-md border bg-slate-950 text-white"
                            value={selectedItemId}
                            onChange={(e) => setSelectedItemId(e.target.value)}
                          >
                            <option value="" className="bg-slate-950 text-white">Select an item...</option>
                            {shopItems.map(item => (
                              <option key={item.id} value={item.id} className="bg-slate-950 text-white">
                                {item.name} ({item.type}) - {item.price_eur}€
                              </option>
                            ))}
                          </select>
                        </div>
                        <Button
                          onClick={giveItem}
                          disabled={!selectedItemId || isProcessingItem}
                          className="w-full"
                        >
                          {isProcessingItem ? "Processing..." : "Give Item"}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {userInventory.map((item) => (
                  <Card key={item.id} className="relative">
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <CardTitle className="text-md">{item.shop_items.name}</CardTitle>
                        <Badge variant={item.is_active ? "default" : "secondary"}>
                          {item.is_active ? "Active" : "Owned"}
                        </Badge>
                      </div>
                      <CardDescription className="text-xs capitalize">{item.shop_items.type}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-4">
                        {item.shop_items.type === 'border' ? (
                          <UserAvatar
                            size="md"
                            borderStyle={item.shop_items.metadata?.style}
                            fallback={(member?.display_name || 'U').charAt(0)}
                          />
                        ) : (
                          <div className="p-2 bg-yellow-500/10 rounded-full">
                            <Zap className="w-5 h-5 text-yellow-500" />
                          </div>
                        )}
                        <div className="text-xs text-muted-foreground">
                          Acquired: {new Date(item.acquired_at).toLocaleDateString()}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {userInventory.length === 0 && (
                  <div className="col-span-full py-12 text-center text-muted-foreground border border-dashed rounded-xl">
                    This user has no items in their inventory.
                  </div>
                )}
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </AdminLayout>
  );
}
